'use client'

import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import {
  getBuilderWidgetById,
  getBuilderWidgetGroups,
  type CatalogWidget,
} from '@/lib/builder-widget-catalog'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'

const CRMS = [
  { name: 'Lasso', src: '/images/home-builders/lasso.png' },
  { name: 'Follow Up Boss', src: '/images/home-builders/fub.png' },
  { name: 'HubSpot', src: '/images/logo-hubspot.svg' },
  { name: 'Salesforce', src: '/images/badges/salesforce.png' },
  { name: 'BuilderTrend', src: '/images/home-builders/buildertrend.png' },
  { name: '+ Custom', src: null },
]

function useWidgetCatalog() {
  const groups = useMemo(() => getBuilderWidgetGroups(), [])
  const [catId, setCatId] = useState(groups[0]?.id ?? '')
  const active = groups.find(g => g.id === catId) ?? groups[0]
  return { groups, catId, setCatId, active }
}

function WidgetFilters({
  groups,
  activeId,
  onSelect,
}: {
  groups: ReturnType<typeof getBuilderWidgetGroups>
  activeId: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="lib-cats" role="tablist" aria-label="Widget categories">
      {groups.map(g => {
        const on = g.id === activeId
        return (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={on}
            className="lib-cat"
            style={on ? { background: g.color, borderColor: g.color, color: '#fff' } : { color: g.color, borderColor: `${g.color}44` }}
            onClick={() => onSelect(g.id)}
          >
            {g.label}
          </button>
        )
      })}
    </div>
  )
}

function WidgetList({
  active,
  selected,
  onToggle,
}: {
  active: ReturnType<typeof getBuilderWidgetGroups>[number]
  selected: Set<number>
  onToggle: (id: number) => void
}) {
  return (
    <div className="lib-list">
      <div className="dim">{active.label}</div>
      <div className="schedule" role="tabpanel" aria-label={active.label}>
        {active.widgets.map(w => {
          const on = selected.has(w.id)
          return (
            <div key={w.id} className={`row row-widget${on ? ' is-on' : ''}`}>
              <div>
                <h4>
                  {w.name}
                  {w.ai ? <em>AI</em> : null}
                </h4>
                <p>{w.desc}</p>
              </div>
              <button
                type="button"
                className="row-add"
                aria-pressed={on}
                onClick={() => onToggle(w.id)}
              >
                {on ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                )}
                {on ? 'Added' : 'Add this widget'}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function InterestSidebar({
  selected,
  onRemove,
}: {
  selected: CatalogWidget[]
  onRemove: (id: number) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!selected.length) {
      setError('Select at least one widget from the list.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const widgetLine = selected.map(w => w.name).join(', ')
      const res = await fetch('/api/form-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          focusArea: `${widgetLine}${note ? `\n\n${note}` : ''}`,
          serviceName: 'Home Builder Widgets',
          source: 'home-builders-lp',
        }),
      })
      if (!res.ok) throw new Error('Submission failed')
      setSubmitted(true)
      trackPostHogEvent('form_submitted', {
        form_source: 'home-builders-widgets',
        service_name: 'Home Builder Widgets',
        widget_count: selected.length,
      })
    } catch {
      setError('Something went wrong — email tomas@smartmetrics.com.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rt rt-sticky">
      <div className="inq-card" id="widget-inquiry">
        <div className="inq">
          <div className="inq-kicker">I am interested in</div>
          {selected.length ? (
            <ul className="inq-picks">
              {selected.map(w => (
                <li key={w.id}>
                  <span>{w.name}</span>
                  <button type="button" aria-label={`Remove ${w.name}`} onClick={() => onRemove(w.id)}>×</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="inq-empty">Select one or more widgets from the list.</p>
          )}
        </div>

      {submitted ? (
        <div className="inq-done">
          <b>Got it — we’ll follow up.</b>
          <p>We’ll review the widgets you picked and send a short scope and timeline.</p>
        </div>
      ) : (
        <form className="inq-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              placeholder="you@buildingco.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Anything we should know? <i>optional</i>
            <textarea
              placeholder="Communities, CRM, timeline…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </label>
          {error ? <p className="inq-err">{error}</p> : null}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send inquiry'}
          </button>
        </form>
      )}
      </div>

      <div className="crms">
        {CRMS.map(c => (
          <div key={c.name}>
            {c.src ? <img src={c.src} alt="" /> : null}
            <span>{c.name}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function HBWidgetsPanel({ lead }: { lead: ReactNode }) {
  const { groups, catId, setCatId, active } = useWidgetCatalog()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const selected = selectedIds
    .map(id => getBuilderWidgetById(id))
    .filter((w): w is CatalogWidget => !!w)
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const toggle = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  if (!active) return null

  return (
    <>
      {selected.length > 0 ? (
        <a className="inq-dock" href="#widget-inquiry">
          Continue to inquiry
          <span>Inquire</span>
        </a>
      ) : null}
      <div className="lib-head fade" key="widgets-head">
        <p className="lead">{lead}</p>
        <WidgetFilters groups={groups} activeId={catId} onSelect={setCatId} />
      </div>
      <div className="lib-body fade" key="widgets-body">
        <WidgetList active={active} selected={selectedSet} onToggle={toggle} />
        <InterestSidebar selected={selected} onRemove={id => toggle(id)} />
      </div>
    </>
  )
}
